import React from 'react';

import { Box, Typography } from '@mui/joy';

export interface GimbalOrientationProps {
  yaw: number; // -180 to 180 degrees
  pitch: number; // -90 to 90 degrees
  roll: number; // -180 to 180 degrees
  heading: number; // drone heading, 0 = North
  fov: number; // camera field-of-view in degrees
  zoom: number;
  isLocked: boolean;
}

const SIZE = 200; // svg viewport size
const CENTER = SIZE / 2;
const BODY_SIZE = 40; // size of the drone body cross
const ARROW_LEN = 70; // length of gimbal direction arrow
const HEADING_ARROW_LEN = 50; // length of the drone heading arrow
const TICK_RADIUS = ARROW_LEN + 10;
const GAUGE_HEIGHT = 120;

const GimbalVisualization: React.FC<GimbalOrientationProps> = ({ yaw, pitch, roll, heading, fov, zoom, isLocked }) => {
  // Convert degrees to radians
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const normalize = (deg: number) => ((deg + 540) % 360) - 180;

  // Gimbal yaw relative to the drone heading
  const relYaw = normalize(yaw - heading);

  // Position of the gimbal direction arrow tip
  const tipX = CENTER + ARROW_LEN * Math.sin(toRad(relYaw));
  const tipY = CENTER - ARROW_LEN * Math.cos(toRad(relYaw));

  // Position of the drone heading arrow tip
  const headX = CENTER + HEADING_ARROW_LEN * Math.sin(toRad(heading));
  const headY = CENTER - HEADING_ARROW_LEN * Math.cos(toRad(heading));

  // Field of view wedge path
  const wedgeRadius = ARROW_LEN;
  const fovStartX = CENTER + wedgeRadius * Math.sin(toRad(relYaw - fov / 2));
  const fovStartY = CENTER - wedgeRadius * Math.cos(toRad(relYaw - fov / 2));
  const fovEndX = CENTER + wedgeRadius * Math.sin(toRad(relYaw + fov / 2));
  const fovEndY = CENTER - wedgeRadius * Math.cos(toRad(relYaw + fov / 2));

  const pitchY = CENTER + GAUGE_HEIGHT / 2 - ((pitch + 90) / 180) * GAUGE_HEIGHT;

  const lockedColor = isLocked ? '#16a34a' : '#38bdf8';

  const ticks = Array.from({ length: 12 }, (_, i) => i * 30);

  return (
    <Box className='flex flex-row items-top'>
      <Box className='mr-4' sx={{ minWidth: 80 }}>
        <Typography level='body-xs'>Heading: {Math.round(heading)}&deg;</Typography>
        <Typography level='body-xs'>Gimbal: {Math.round(yaw)}&deg;</Typography>
        <Typography level='body-xs'>Pitch: {Math.round(pitch)}&deg;</Typography>
        <Typography level='body-xs'>Roll: {Math.round(roll)}&deg;</Typography>
        <Typography level='body-xs'>Zoom: {zoom.toFixed(1)}x</Typography>
      </Box>

      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className='transition-transform duration-300'>
        {/* Drone body cross, rotates with roll */}
        <g transform={`rotate(${roll} ${CENTER} ${CENTER})`} style={{ transition: 'transform 0.3s ease' }}>
          <line x1={CENTER - BODY_SIZE} y1={CENTER} x2={CENTER + BODY_SIZE} y2={CENTER} stroke='#bbb'
strokeWidth={4} />
          <line x1={CENTER} y1={CENTER - BODY_SIZE} x2={CENTER} y2={CENTER + BODY_SIZE} stroke='#bbb'
strokeWidth={4} />
        </g>

        {/* Yaw tick marks */}
        <g stroke='#666' strokeWidth={1}>
          {ticks.map((d) => {
            const x1 = CENTER + TICK_RADIUS * Math.sin(toRad(d));
            const y1 = CENTER - TICK_RADIUS * Math.cos(toRad(d));
            const x2 = CENTER + (TICK_RADIUS - 5) * Math.sin(toRad(d));
            const y2 = CENTER - (TICK_RADIUS - 5) * Math.cos(toRad(d));
            return <line key={d} x1={x1} y1={y1} x2={x2} y2={y2} />;
          })}
        </g>
        <g fill='#666' fontSize='8' textAnchor='middle' dominantBaseline='central'>
          {[0, 90, 180, 270].map((d) => {
            const tx = CENTER + (TICK_RADIUS + 8) * Math.sin(toRad(d));
            const ty = CENTER - (TICK_RADIUS + 8) * Math.cos(toRad(d));
            return (
              <text key={d} x={tx} y={ty}>
                {d}
              </text>
            );
          })}
        </g>

        {/* Field of view wedge */}
        <path
          d={`M ${CENTER} ${CENTER} L ${fovStartX} ${fovStartY} A ${wedgeRadius} ${wedgeRadius} 0 0 1 ${fovEndX} ${fovEndY} Z`}
          fill={lockedColor}
          fillOpacity={0.2}
          stroke={lockedColor}
          strokeWidth={1.5}
          style={{ transition: 'd 0.3s ease' }}
        />

        {/* Gimbal direction arrow */}
        <line
          x1={CENTER}
          y1={CENTER}
          x2={tipX}
          y2={tipY}
          stroke={lockedColor}
          strokeWidth={3}
          markerEnd='url(#arrowhead)'
          style={{ transition: 'all 0.3s ease' }}
        />

        {/* Drone heading arrow */}
        <line
          x1={CENTER}
          y1={CENTER}
          x2={headX}
          y2={headY}
          stroke='#fbbf24'
          strokeWidth={2}
          markerEnd='url(#droneArrow)'
          style={{ transition: 'all 0.3s ease' }}
        />

        {/* Arrow head definitions */}
        <defs>
          <marker id='arrowhead' markerWidth='6' markerHeight='6' refX='3' refY='3'
orient='auto'>
            <polygon points='0 0, 6 3, 0 6' fill={lockedColor} />
          </marker>
          <marker id='droneArrow' markerWidth='6' markerHeight='6' refX='3' refY='3'
orient='auto'>
            <polygon points='0 0, 6 3, 0 6' fill='#fbbf24' />
          </marker>
        </defs>

        {/* Pitch gauge on right side */}
        <g>
          <line
            x1={SIZE - 20}
            y1={CENTER - GAUGE_HEIGHT / 2}
            x2={SIZE - 20}
            y2={CENTER + GAUGE_HEIGHT / 2}
            stroke='#666'
            strokeWidth={2}
          />
          <circle cx={SIZE - 20} cy={pitchY} r={6} fill={lockedColor} />
        </g>
      </svg>
    </Box>
  );
};

export default GimbalVisualization;
